import React, { useState } from "react";
import { WebhookConfig } from "@/api/entities";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Webhook as WebhookIcon } from "lucide-react";
import WebhookCard from "../components/webhooks/WebhookCard";
import WebhookDialog from "../components/webhooks/WebhookDialog";

export default function Webhooks() {
  const [showDialog, setShowDialog] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const queryClient = useQueryClient();

  const { data: webhooks = [] } = useQuery({
    queryKey: ['webhooks'],
    queryFn: () => WebhookConfig.list({}, { orderBy: 'created_date', orderDirection: 'desc' }),
  });

  const createMutation = useMutation({
    mutationFn: (data) => WebhookConfig.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      setShowDialog(false);
      setEditingWebhook(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => WebhookConfig.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      setShowDialog(false);
      setEditingWebhook(null);
    },
  });

  const handleSubmit = (data) => {
    if (editingWebhook) {
      updateMutation.mutate({ id: editingWebhook.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (webhook) => {
    setEditingWebhook(webhook);
    setShowDialog(true);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Webhooks</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Configura integraciones con sistemas externos</p>
          </div>
          <Button 
            onClick={() => {
              setEditingWebhook(null);
              setShowDialog(true);
            }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Webhook
          </Button>
        </div>
      </div>

      {webhooks.length === 0 ? (
        <Card className="dark:bg-gray-900 dark:border-gray-800">
          <CardContent className="py-12 text-center">
            <WebhookIcon className="w-16 h-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No hay webhooks configurados
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Crea tu primer webhook para integrar con sistemas externos
            </p>
            <Button onClick={() => setShowDialog(true)} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              Crear Webhook
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {webhooks.map((webhook) => (
            <WebhookCard
              key={webhook.id}
              webhook={webhook}
              onEdit={handleEdit}
              onToggleActive={(id, activo) => 
                updateMutation.mutate({ id, data: { activo } })
              }
            />
          ))}
        </div>
      )}

      <WebhookDialog
        open={showDialog}
        onClose={() => {
          setShowDialog(false);
          setEditingWebhook(null);
        }}
        onSubmit={handleSubmit}
        webhook={editingWebhook}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
}